




import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import Chart, { ChartTooltipModel } from "chart.js";
import ColorHelper from "@/services/ColorHelper";
import NumberFormatter from "@/services/NumberFormatter";

export interface CustomChartItem {
  label: string;
  color?: string;
  level?: number;
  borderColor?: string;
  borderDash?: number[];
  pointBackgroundColor?: string;
  pointBorderColor?: string;
  data: number[];
}

export const CustomChartPieDefaultOptions: Chart.ChartOptions = {
  layout: {
    padding: {
      left: 32,
      right: 32,
      top: 0,
      bottom: 0,
    },
  },
  legend: {
    position: "right",
    align: "center",
    labels: {
      padding: 11,
    },
  },
};

export const CustomChartDoughnutDefaultOptions: Chart.ChartOptions = {
  cutoutPercentage: 70,
  layout: {
    padding: {
      left: 32,
      right: 32,
      top: 0,
      bottom: 0,
    },
  },
  legend: {
    position: "right",
    align: "center",
    labels: {
      padding: 11,
    },
  },
};

@Component
export default class CustomChart extends Vue {
  @Prop({ default: "bar" }) readonly type?: string;
  @Prop({ default: false }) readonly stacked?: boolean;
  @Prop({ default: false }) readonly percentage?: boolean;
  @Prop({ default: false }) readonly decimals?: boolean;
  @Prop({ default: 90 }) readonly pointsLimit?: number;
  @Prop({ default: true }) readonly showLegend!: boolean;
  @Prop({ default: true }) readonly beginAtZero!: boolean;
  @Prop({ default: false }) readonly multilevel!: boolean;
  @Prop({ default: false }) readonly hideSublevelLabels!: boolean;

  @Prop() readonly labels?: string[];
  @Prop() readonly items!: Array<CustomChartItem>;
  @Prop() readonly options?: Chart.ChartOptions;
  @Prop() readonly customTooltipFn?: (
    chartCanvas: HTMLCanvasElement
  ) => (tooltipModel: ChartTooltipModel) => any;

  @Watch("type")
  onTypeChange() {
    this.reload();
  }

  @Watch("stacked")
  onValueChange() {
    if (this.chart) {
      this.chart.options = this.chartOptions;

      if (this.isLine) {
        this.chart.data.datasets = this.getChartDataSets();
      }

      this.updateChart();
    }
  }

  @Watch("items", { deep: true })
  onItemsChange() {
    if (this.chart) {
      this.chart.data.labels = this.chartLabels;
      this.chart.data.datasets = this.getChartDataSets();
      this.chart.options = this.chartOptions;
      this.updateChart();
    }
  }

  @Watch("percentage")
  onPercentageChange() {
    if (this.chart) {
      this.chart.options = this.chartOptions;
      this.onItemsChange();
    }
  }

  chart!: Chart;
  hiddenItems: number[] = [];

  get isBar(): boolean {
    return this.type === "bar";
  }

  get isPie(): boolean {
    return this.type === "pie";
  }

  get isLine(): boolean {
    return this.type === "line";
  }

  get isDoughnut(): boolean {
    return this.type === "doughnut";
  }

  get isCircular(): boolean {
    return this.isPie || this.isDoughnut;
  }

  mounted() {
    this.createChart();
  }

  createChart() {
    this.chart = new Chart(
      this.$refs.chart as HTMLCanvasElement,
      this.chartConfiguration
    );
  }

  reload() {
    this.chart.destroy();
    this.createChart();
    this.updateItemVisibility();
  }

  updateItemVisibility() {
    this.chart?.data?.datasets?.forEach((e, i) => {
      const meta = this.chart.getDatasetMeta(i);
      const hiddenIndex = this.hiddenItems.indexOf(i);

      if (hiddenIndex !== -1) {
        meta.hidden = true;
      }
    });
  }

  updateChart() {
    this.updateItemVisibility();
    this.chart.update();
  }

  get chartConfiguration(): Chart.ChartConfiguration {
    return {
      type: this.type,
      data: {
        labels: this.chartLabels,
        datasets: this.getChartDataSets(),
      },
      options: this.chartOptions,
    };
  }

  get chartOptions(): Chart.ChartOptions {
    if (this.options) {
      return this.options;
    }

    if (this.isPie) {
      if (this.multilevel) {
        const mainLabels = this.hideSublevelLabels
          ? this.items.filter((item) => (item.level ?? 0) === 0)
          : this.items;
        return {
          ...CustomChartPieDefaultOptions,
          legend: {
            ...CustomChartPieDefaultOptions.legend,
            onClick: () => null,
            labels: {
              filter: (label) => (label.index ?? 0) < mainLabels.length,
            },
          },
        };
      }
      return CustomChartPieDefaultOptions;
    }

    if (this.isDoughnut) {
      if (this.multilevel) {
        const mainLabels = this.hideSublevelLabels
          ? this.items.filter((item) => (item.level ?? 0) === 0)
          : this.items;
        const maxLevel = this.items.reduce(
          (acc, item) => (acc > (item.level ?? 0) ? acc : item.level ?? 0),
          0
        );
        return {
          ...CustomChartDoughnutDefaultOptions,
          cutoutPercentage: 70 - 20 * maxLevel,
          legend: {
            ...CustomChartDoughnutDefaultOptions.legend,
            onClick: () => null,
            labels: {
              filter: (label) => (label.index ?? 0) < mainLabels.length,
            },
          },
        };
      }
      return CustomChartDoughnutDefaultOptions;
    }

    const customTooltipConfig = this.customTooltipFn
      ? {
          enabled: false,
          custom: this.customTooltipFn(this.$refs.chart as HTMLCanvasElement),
        }
      : {};

    return {
      scales: {
        yAxes: [
          {
            stacked: this.stacked,
            ticks: {
              beginAtZero: this.beginAtZero,
              callback: this.formatYAxesLabel,
              max: this.percentage ? 100 : undefined,
              min: this.percentage ? 0 : undefined,
              stepSize: this.percentage ? 10 : undefined,
              precision: 0,
            } as Chart.LinearTickOptions,
          },
        ],
        xAxes: [
          {
            stacked: this.stacked,
          },
        ],
      },
      legend: {
        display: this.showLegend,
        onClick: (event, item) => {
          const index = item.datasetIndex;

          if (index !== undefined) {
            this.toggleItemVisibility(index);
          }
        },
      },
      tooltips: {
        callbacks: {
          beforeLabel: (tooltipItem, data) => {
            const extra: any = (
              data.datasets![tooltipItem.datasetIndex!] as any
            ).extra;
            return extra
              ? extra.tooltips[tooltipItem.index!].beforeLabel
              : null;
          },
          label: (tooltipItem, data) => {
            const extra: any = (
              data.datasets![tooltipItem.datasetIndex!] as any
            ).extra;
            const defaultLabel = `${
              data.datasets![tooltipItem.datasetIndex!].label
            }: ${tooltipItem.value}`;
            return extra
              ? extra.tooltips[tooltipItem.index!].label
              : defaultLabel;
          },
        },
        ...customTooltipConfig,
      },
    };
  }

  getChartDataSets(): Chart.ChartDataSets[] {
    ColorHelper.reset();

    if (this.isCircular) {
      return this.getPieDataSet();
    }

    const items = this.percentage ? this.getItemsPercentage() : this.items;

    return items.map((item: CustomChartItem) => {
      const color = item.color || ColorHelper.pick();
      const borderColor =
        item.borderColor ||
        (this.isLine && this.stacked ? ColorHelper.adjust(color, -32) : color);
      const pointBackgroundColor = item.pointBackgroundColor || color;
      const pointBorderColor =
        item.pointBorderColor || (this.stacked ? "#ffffff" : color);

      let data = item.data;
      const nonZeroData = item.data.filter((n: number) => n != 0);
      if (nonZeroData.length == 0) {
        data = [];
      }
      const optional = item.borderDash ? { borderDash: item.borderDash } : {};
      const extra = (item as any).extra;

      return {
        label: item.label,
        borderColor,
        backgroundColor: color,
        data: data,
        maxBarThickness: 26,
        categoryPercentage: 0.5,
        fill: !this.isLine || (this.isLine && this.stacked),
        lineTension: 0.2,
        borderWidth: 2,
        pointRadius: this.pointsLimit && this.pointsLimit < data.length ? 0 : 2,
        pointBorderWidth: 1,
        pointBackgroundColor,
        pointBorderColor,
        ...optional,
        ...(extra ? { extra } : {}),
      };
    });
  }

  getPieDataSet(): Chart.ChartDataSets[] {
    if (this.multilevel) {
      return this.getMultilevelPieDataSet();
    }
    let data: number[] = [];
    const colors: string[] = [];

    this.items?.forEach((item: CustomChartItem) => {
      const color = item.color || ColorHelper.pick();
      colors.push(color);

      const total = item.data.reduce((previous: number, current: number) => {
        return previous + parseFloat(current as unknown as string);
      }, 0);

      data.push(total);
    });

    if (this.percentage) {
      const total = data.reduce((previous: number, current: number) => {
        return previous + parseFloat(current as unknown as string);
      }, 0);

      data = data.map((value: number) => {
        return Math.round((value / total) * 100);
      });
    }

    return [
      {
        data: data,
        backgroundColor: colors,
      },
    ];
  }

  getMultilevelPieDataSet() {
    const colors: string[] = [];
    const maxLevel = this.items.reduce(
      (acc, item) => (acc > (item.level ?? 0) ? acc : item.level ?? 0),
      0
    );
    const chartData: Chart.ChartDataSets[] = [];
    let itemsInPreviousLevels = 0;
    for (let currentLevel = 0; currentLevel <= maxLevel; currentLevel++) {
      const levelData = this.items
        .filter((item) => (item.level ?? 0) === currentLevel)
        .map((item) => {
          const color = item.color || ColorHelper.pick();
          colors.push(color);
          const total = item.data.reduce(
            (previous: number, current: number) => {
              return previous + parseFloat(current as unknown as string);
            },
            0
          );
          return total;
        });
      chartData.push({
        data: [
          ...new Array(itemsInPreviousLevels).fill(0),
          ...levelData,
          ...new Array(
            this.items.length + itemsInPreviousLevels + levelData.length
          ).fill(0),
        ],
        backgroundColor: colors,
      });
      itemsInPreviousLevels += levelData.length;
    }
    return chartData;
  }

  get chartLabels(): string[] | undefined {
    if (this.isCircular) {
      const labels: string[] = [];

      this.items?.forEach((item: CustomChartItem) => {
        labels.push(item.label);
      });

      return labels;
    } else {
      return this.labels;
    }
  }

  getItemsPercentage(): CustomChartItem[] {
    if (!this.labels) {
      return [];
    }

    const totals = this.labels.map(() => {
      return 0;
    });

    const items = this.getClonedItems();

    items.map((item: CustomChartItem) => {
      item.data.map((value: number, index: number) => {
        if (typeof totals[index] !== undefined) {
          totals[index] += value;
        }
      });
    });

    return items.map((item: CustomChartItem) => {
      item.data = item.data.map((value: number, index: number) => {
        if (totals[index]) {
          return Math.round((value / totals[index]) * 100);
        } else {
          return value;
        }
      });

      return item;
    });
  }

  getClonedItems(): CustomChartItem[] {
    const items: CustomChartItem[] = [];
    this.items.forEach((item) => items.push(Object.assign({}, item)));

    return items;
  }

  formatYAxesLabel(value: number): string | number | null | undefined {
    if (this.percentage) {
      return `${value.toString()}%`;
    } else if (this.decimals) {
      return NumberFormatter.beautify(value, "0", "-", true, 2);
    } else {
      return NumberFormatter.beautify(value);
    }
  }

  toggleItemVisibility(index: number) {
    const alreadyHidden =
      this.chart.getDatasetMeta(index).hidden === null
        ? false
        : this.chart.getDatasetMeta(index).hidden;

    this.chart?.data?.datasets?.forEach((e, i) => {
      const meta = this.chart.getDatasetMeta(i);

      if (i == index) {
        if (alreadyHidden) {
          meta.hidden = false;
          const hiddenIndex = this.hiddenItems.indexOf(index);
          this.hiddenItems.splice(hiddenIndex, 1);

          this.$emit("item-visible", index);
        } else {
          meta.hidden = true;
          this.hiddenItems.push(index);
          this.$emit("item-hidden", index);
        }
      }
    });

    this.chart.update();
  }
}
